import PropTypes from 'prop-types';
import { Box, Text } from 'theme-ui';
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useWindowSize from '~/hooks/components/use-window-size.js';
import useMatchMedia from '~/hooks/utils/use-match-media';

type Props = {
  title: string | null | undefined;
  children: React.ReactNode;
  closedOnStart?: boolean;
};

const CollapsiblePanel = ({ title, children, closedOnStart }: Props) => {
  const detailsRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useMatchMedia('(max-width: 767px)');
  const [isOpen, setIsOpen] = useState(!closedOnStart);

  const answerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);
  const window = useWindowSize();

  useEffect(() => {
    if (answerRef.current) {
      setHeight(answerRef.current.offsetHeight);
    }
  }, [answerRef, isOpen, window]);

  const spring = {
    type: 'spring',
    damping: 20,
    stiffness: 100,
  };
  const child = {
    type: 'spring',
    damping: 20,
    stiffness: 100,
    delay: 0.3,
  };

  useEffect(() => {
    if (!isMobile) {
      setIsOpen(closedOnStart ? false : true);
    } else {
      setIsOpen(false);
    }
  }, [isMobile, closedOnStart]);

  useEffect(() => {
    const detailsRefCurrent = detailsRef.current;
    if (isOpen) {
      detailsRefCurrent?.setAttribute('data-open', 'true');
      return;
    } else {
      detailsRefCurrent?.removeAttribute('data-open');
    }
    return () => {
      detailsRefCurrent?.removeAttribute('data-open');
    };
  }, [isOpen]);

  return (
    <Box
      sx={{
        '&[data-open] summary::after': {
          content: '"—"',
        },
        ':not([data-open]) summary::after': {
          content: '"+"',
        },
        'summary::-webkit-details-marker': {
          display: 'block',
        },

        variant: 'text.copy',
        strong: {
          display: 'inline-block',
          mb: '.8rem',
          fontWeight: 'medium',
        },
        ul: {
          p: 0,
          mt: 0,
          listStyle: 'outside',
          ml: '1.6rem',
        },
        li: {
          mb: '.8rem',
          ml: '1.6rem',
        },
        p: {
          mb: 0,
        },
        'p:first-of-type, ul:first-of-type': {
          mt: ['2.4rem', '3.2rem'],
        },
      }}
      ref={detailsRef}
    >
      <Text
        as="summary"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          variant: 'text.copy',
          display: 'flex',
          flexFlow: 'row nowrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          textTransform: 'uppercase',
          fontWeight: 500,
          lineHeight: 1,
          cursor: 'pointer',
          outline: 'none',
          pt: '1.6rem',
          borderTop: '1px solid',
          borderColor: 'cloud',
          '::marker': { content: '""' },
          '::after': { content: '""' },
        }}
      >
        {title}
      </Text>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={spring}
            animate={{
              height: height,
              overflow: 'visible',
            }}
            exit={{
              height: 0,
              overflow: 'hidden',
            }}
            initial={{ height: '0', overflow: 'hidden' }}
          >
            <motion.div
              transition={child}
              initial={{ height: isOpen ? height : 0, opacity: 0, transform: 'translateY(-7%) rotateX(25deg)' }}
              animate={{ height: height, opacity: 1, transform: 'translateY(0%) rotateX(0deg)' }}
              style={{ opacity: 0 }}
            >
              <div ref={answerRef}>{children}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

CollapsiblePanel.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

export default CollapsiblePanel;
